import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

export type CmsBlock = Tables<'cms_blocks'>;
export type CmsBlockInsert = TablesInsert<'cms_blocks'>;
export type CmsBlockUpdate = TablesUpdate<'cms_blocks'>;

export interface UseCmsBlocksReturn {
  blocks: CmsBlock[];
  loading: boolean;
  error: string | null;
  createBlock: (block: Omit<CmsBlockInsert, 'user_id'>) => Promise<void>;
  updateBlock: (id: string, updates: CmsBlockUpdate) => Promise<void>;
  deleteBlock: (id: string) => Promise<void>;
  refreshBlocks: () => Promise<void>;
  isAdmin: boolean;
}

export function useCmsBlocks(): UseCmsBlocksReturn {
  const [blocks, setBlocks] = useState<CmsBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Sprawdź czy użytkownik to administrator
  const checkAdminStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAdmin(user?.email === 'cesarzdarek@gmail.com');
    } catch (err) {
      console.error('Error checking admin status:', err);
      setIsAdmin(false);
    }
  };

  // Pobierz bloki z bazy danych
  const fetchBlocks = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('cms_blocks')
        .select('*')
        .eq('status', 'active')
        .order('sort_order', { ascending: true });

      if (fetchError) {
        throw fetchError;
      }

      setBlocks(data || []);
    } catch (err) {
      console.error('Error fetching CMS blocks:', err);
      setError(err instanceof Error ? err.message : 'Wystąpił błąd podczas pobierania bloków');
    } finally {
      setLoading(false);
    }
  };

  // Utwórz nowy blok
  const createBlock = async (blockData: Omit<CmsBlockInsert, 'user_id'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Użytkownik nie jest zalogowany');
      }

      if (!isAdmin) {
        throw new Error('Brak uprawnień do tworzenia bloków');
      }

      const { error: insertError } = await supabase
        .from('cms_blocks')
        .insert({
          ...blockData,
          user_id: user.id,
        });

      if (insertError) {
        throw insertError;
      }

      // Odśwież listę bloków
      await fetchBlocks();
    } catch (err) {
      console.error('Error creating CMS block:', err);
      setError(err instanceof Error ? err.message : 'Wystąpił błąd podczas tworzenia bloku');
      throw err;
    }
  };

  // Zaktualizuj blok
  const updateBlock = async (id: string, updates: CmsBlockUpdate) => {
    try {
      if (!isAdmin) {
        throw new Error('Brak uprawnień do edycji bloków');
      }

      const { error: updateError } = await supabase
        .from('cms_blocks')
        .update(updates)
        .eq('id', id);

      if (updateError) {
        throw updateError;
      }

      // Odśwież listę bloków
      await fetchBlocks();
    } catch (err) {
      console.error('Error updating CMS block:', err);
      setError(err instanceof Error ? err.message : 'Wystąpił błąd podczas aktualizacji bloku');
      throw err;
    }
  };

  // Usuń blok
  const deleteBlock = async (id: string) => {
    try {
      if (!isAdmin) {
        throw new Error('Brak uprawnień do usuwania bloków');
      }

      const { error: deleteError } = await supabase
        .from('cms_blocks')
        .delete()
        .eq('id', id);

      if (deleteError) {
        throw deleteError;
      }

      // Odśwież listę bloków
      await fetchBlocks();
    } catch (err) {
      console.error('Error deleting CMS block:', err);
      setError(err instanceof Error ? err.message : 'Wystąpił błąd podczas usuwania bloku');
      throw err;
    }
  };

  // Odśwież bloki
  const refreshBlocks = async () => {
    await fetchBlocks();
  };

  // Inicjalizacja
  useEffect(() => {
    checkAdminStatus();
    fetchBlocks();
  }, []);

  return {
    blocks,
    loading,
    error,
    createBlock,
    updateBlock,
    deleteBlock,
    refreshBlocks,
    isAdmin,
  };
}
